import type {
    Author, Category, ContentfulSector, NormalizedContent, NormalizedPersistedSectorsState, Product,
    SearchableTicker, SimplePageContent
} from "@tmi/types/entities";
import { openDatabase, runTransaction, wrapCacheError } from "./cache-database";
import type { StoreName } from "./cache-schema";
import { createStateStore, createStore, type CacheStore } from "./cache-store";

export type { CacheStore, StateStore } from "./cache-store";
export { CacheError, CacheClosedError, CacheOpenError } from "./cache-database";
export type { StoreName } from "./cache-schema";

/**
 * The one readiness handle. Every store method awaits the open internally; call `cache.ready()` explicitly
 * only where you want a timeout around the open (see stores/SectorHistory.ts). Rejects with CacheClosedError
 * once a newer build has upgraded the database (a reload is in flight) — callers must treat that as "stop
 * quietly", see the Wave 2 rules.
 */
export const cache = {
    ready: (): Promise< void > => openDatabase().then( () => undefined ),
    transaction
};

/**
 * `runTransaction` behind the same contract as every store method: a failure is always a CacheError carrying
 * `cause` — a failed request, or a throw from `work` — and a CacheError subclass passes through unchanged (F-M3).
 */
function transaction< R >(
    stores: readonly StoreName[],
    mode: IDBTransactionMode,
    work: ( tx: IDBTransaction ) => R | Promise< R >
): Promise< R > {
    return wrapCacheError( `transaction over [ ${ stores.join( ', ' ) } ]`, () => runTransaction( stores, mode, work ) );
}

/** Awaited before every operation on a user-scoped store (see setUserScopedCacheGate). Unset in the worker. */
let userScopedGate: ( () => Promise< unknown > ) | null = null;

/**
 * UX roadmap #63 (boot-5): the main thread sets this to its cache owner check (cache-owner.ts), so no user-scoped
 * store (USER_SCOPED_CONTENT_STORES) is read or written before the cache is known to belong to the member signed in.
 * Many views load those stores themselves, some before the boot's private phase, and each used to read the previous
 * member's rows on a shared browser. A rejected gate rejects the operation with its CacheError. `null` removes it.
 */
export function setUserScopedCacheGate( gate: ( () => Promise< unknown > ) | null ): void {
    userScopedGate = gate;
}

function userScoped< T >( store: CacheStore< T > ): CacheStore< T > {
    const behindGate = < A extends unknown[], R >( operation: ( ...args: A ) => Promise< R > ) =>
        async ( ...args: A ): Promise< R > => {
            const gate = userScopedGate;
            if ( gate ) {
                // Same contract as the operation itself: a failure is a CacheError carrying `cause`.
                await wrapCacheError( `${ store.name }.owner`, gate );
            }
            return operation( ...args );
        };

    return {
        name: store.name,
        keyField: store.keyField,
        all: behindGate( store.all ),
        keys: behindGate( store.keys ),
        get: behindGate( store.get ),
        count: behindGate( store.count ),
        isEmpty: behindGate( store.isEmpty ),
        put: behindGate( store.put ),
        putMany: behindGate( store.putMany ),
        deleteMany: behindGate( store.deleteMany ),
        clear: behindGate( store.clear )
    };
}

// The user-scoped bindings (USER_SCOPED_CONTENT_STORES in cache-schema.ts) sit behind the owner gate.
export const articles      = userScoped( createStore< NormalizedContent, 'contentfulId' >( 'articles', 'contentfulId' ) );
export const authors       = createStore< Author, 'id' >( 'authors', 'id' );
export const bonusReports  = userScoped( createStore< NormalizedContent, 'contentfulId' >( 'reports', 'contentfulId' ) );
export const categories    = createStore< Category, 'id' >( 'categories', 'id' );
export const products      = createStore< Product, 'id' >( 'products', 'id' );
export const sectors       = createStore< ContentfulSector, 'contentfulId' >( 'sectors', 'contentfulId' );
export const sectorHistory = userScoped( createStore< NormalizedPersistedSectorsState, 'dt' >( 'history', 'dt' ) );
export const simplePages   = createStore< SimplePageContent, 'id' >( 'pages', 'id' );
export const syncState     = createStateStore( 'sync_state' );
export const tickers       = createStore< SearchableTicker, 'id' >( 'tickers', 'id' );
export const tradeAlerts   = userScoped( createStore< NormalizedContent, 'contentfulId' >( 'alerts', 'contentfulId' ) );
export const webinars      = userScoped( createStore< NormalizedContent, 'contentfulId' >( 'webinars', 'contentfulId' ) );
